import { useEffect, useState } from "react";
import { FoodsSelect } from "@foodpilot/foods";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getPropertyDefaultOption, getPropertyGenericOption, findOptionByCode } from "src/utils/dataListOptions";
import { FormPropertyProps } from "../../../FormProperty";
import { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";
import { usePropertyContext } from "../../../context/usePropertyContext";
import { FieldValueLayout } from "../../FieldValueLayout";
import { saveOrDeleteOptionAnswer } from "../saveOrDeleteOptionAnswer";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";
import { useQuestionnaireFormContext } from "src/components/QuestionnaireForm/context/useQuestionnaireForm";

type SsqSelectProps = FormPropertyProps & {
    options: DataListOption[];
};

export const SsqSelect = (props: SsqSelectProps) => {
    const { t } = useTranslation();
    const { property } = props;

    return (
        <Stack direction="column" gap="8px">
            <SsqSelectComponent {...props} />
            {property.readonly && (
                <Typography variant="small-medium">{t("cette_valeur_est_en_lecture_seule")}</Typography>
            )}
        </Stack>
    );
};

const SsqSelectComponent = (props: SsqSelectProps) => {
    const { t } = useTranslation();
    const { accessibleLabel, descriptionId, invalid, property, position, options, dimensionValue, showSubscore } =
        props;
    const { appearance = "legacy" } = useQuestionnaireFormContext();

    const { post, getElement, onUpdate, onDelete, canEdit } = usePropertyContext();
    const element = getElement(property.id, position);

    const propertyDefaultOption = getPropertyDefaultOption(property, options);
    const propertyGenericOption = getPropertyGenericOption(property, options);

    // Initialize with either the element value or default value
    const selectedCode = element?.dataListOptionCodes?.[0];
    const initialValue: DataListOption | undefined = findOptionByCode(options, selectedCode);

    const [genericValueSelected, setGenericValueSelected] = useState<boolean>(element?.isGenericValue ?? false);
    const [value, setValue] = useState<DataListOption | null>(initialValue ?? null);

    useEffect(() => {
        setValue(initialValue ?? null);
    }, [initialValue]);

    if (post === undefined) return;

    const updatePropertyValue = (value: DataListOption | null, isGenericValue?: boolean) => {
        saveOrDeleteOptionAnswer(
            { onUpdate, onDelete },
            { postId: post.id, propertyId: Number(property.id), position: Number(props.position ?? 0) },
            value,
            isGenericValue,
        );
    };

    if (post === undefined) return;

    const select = (
        <FoodsSelect
            selectedOption={value}
            options={options}
            onChange={(selectedValue: DataListOption | null) => {
                setValue(selectedValue);
                updatePropertyValue(selectedValue);
            }}
            getId={(item) => item.id}
            getName={(item) => item.label}
            disabled={canEdit === false || property.readonly || genericValueSelected}
            textOptions={{
                placeholder: t("component.select.placeholder"),
                searchPlaceholder: t("component.select.searchplaceholder"),
                emptyOptionsText: t("component.select.nooptions"),
                defaultValueText: t("component.select.defaultvalue"),
                noResult: t("component.select.noresultfound"),
            }}
            BoxProps={{
                width: "100%",
                mindWidth: "100%",
                // border: `2px solid blue`,
            }}
            noValue={t("component.select.unselectvalue")}
        />
    );

    return (
        <FieldValueLayout
            property={property}
            element={element}
            layoutOptions={{
                forceHideScore: showSubscore === false,
                isGenericValueSelected: genericValueSelected,
                defaultValue: propertyDefaultOption?.label ?? null,
                genericValue: propertyGenericOption?.label ?? null,
                setDefaultValue: () => {
                    if (propertyDefaultOption === undefined) return;
                    if (value?.id === propertyDefaultOption.id) {
                        return;
                    }

                    setValue(propertyDefaultOption);
                    updatePropertyValue(propertyDefaultOption);
                },
                setGenericValue: () => {
                    if (propertyGenericOption === undefined) return;

                    setGenericValueSelected((prevState) => !prevState);
                    setValue(!genericValueSelected ? propertyGenericOption : null);
                    updatePropertyValue(!genericValueSelected ? propertyGenericOption : null, !genericValueSelected);
                },
                dimensionValue,
                hideUnit: true,
            }}
        >
            {appearance === "product-v2" ?
                <FoodsSelectTriggerAdapter
                    accessibleLabel={accessibleLabel ?? property.title}
                    descriptionId={descriptionId}
                    invalid={invalid}
                >
                    {select}
                </FoodsSelectTriggerAdapter>
            :   select}
        </FieldValueLayout>
    );
};
